import { api } from '@services/api';
import { handleError } from '../../utils/api-error-handling';
import { encryptPayload } from '../../utils/security';
import { frontOfficeApi } from './front-office.api.rtk';

export const roomPostingApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getEntries: builder.query({
      query: () => 'roomposting/entries',
      providesTags: ['RoomPosting'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    getLedgerList: builder.query({
      query: (params) => {
        const baseQuery = `roomposting/ledger/${params?.transactionIdentifier || 'Nan'}/${params?.transactionType}`;
        const queryParams = [
          params?.isCityLedgerPosting ? 'isCustomerLedger=1' : '',
          params?.showIsClosed ? 'showIsClosed=1' : '',
        ]
          .filter(Boolean)
          .join('&');
        return `${baseQuery}${queryParams ? '?' + queryParams : ''}`;
      },
      providesTags: ['Ledger'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),
    getGuestLedger: builder.query({
      query: (params) => `roomposting/guest-ledger/${params}`,
      providesTags: ['GuestLedger'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),

    postTransactionEntries: builder.mutation({
      query: (payload) => {
        const { invalidateTags, ...body } = payload;
        return {
          url: 'roomposting/ledger-entry',
          method: 'POST',
          body: encryptPayload(body),
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (!arg?.invalidateTags) {
            dispatch(
              api.util.invalidateTags([
                'RoomPosting',
                'Bookings',
                'Specific Booking',
              ]),
            );
            return;
          }
          dispatch(api.util.invalidateTags(['RoomPosting']));
        } catch (err) {
          // optional error handling
        }
      },
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
    }),

    getFolioUuidByRoomNo: builder.query({
      query: (params) => `roomposting/reference/${params}`,
      providesTags: ['RoomPosting'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),

    getSubGroupList: builder.query({
      query: () => 'roomposting/subgroup',
      providesTags: ['RoomPosting'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),

    getInHouseGuest: builder.query({
      query: (params) => `roomposting/inhouse/${params}`,
      providesTags: ['RoomPosting'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),

    postTransferSubGroup: builder.mutation({
      query: (payload) => ({
        url: 'roomposting/transfer-subgroup',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      invalidatesTags: ['RoomPosting', 'Bookings', 'Specific Booking'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
    }),

    postModifyServiceCharge: builder.mutation({
      query: (payload) => ({
        url: 'roomposting/service-charge',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      invalidatesTags: ['RoomPosting', 'Bookings', 'Specific Booking'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
    }),

    postModifyItemRemarks: builder.mutation({
      query: (payload) => ({
        url: 'roomposting/item-remarks',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      invalidatesTags: ['RoomPosting', 'Bookings', 'Specific Booking'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
    }),

    postRegularDiscount: builder.mutation({
      query: (payload) => ({
        url: 'roomposting/regular-discount',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      invalidatesTags: ['RoomPosting', 'Bookings', 'Specific Booking'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
    }),

    postSpecialDiscount: builder.mutation({
      query: (payload) => ({
        url: 'roomposting/special-discount',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      invalidatesTags: ['RoomPosting', 'Bookings', 'Specific Booking'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
    }),

    postLineTransfer: builder.mutation({
      query: (payload) => ({
        url: 'roomposting/line-transfer',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      invalidatesTags: ['RoomPosting', 'Bookings', 'Specific Booking'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
    }),

    postFolioItemVoid: builder.mutation({
      query: (payload) => {
        const { invalidateTags, ...body } = payload;
        return {
          url: 'roomposting/item-void',
          method: 'POST',
          body: encryptPayload(body),
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Run this code for successful 2xx status codes
          if (!arg?.invalidateTags) {
            dispatch(
              frontOfficeApi.util.invalidateTags([
                'RoomPosting',
                'Bookings',
                'Specific Booking',
              ]),
            );
            return;
          }
          dispatch(api.util.invalidateTags(['RoomPosting']));
        } catch (error) {
          // Handle error without invalidating tags
        }
      },
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
    }),

    postFolioRemoveDiscount: builder.mutation({
      query: (payload) => ({
        url: 'roomposting/remove-discount',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      invalidatesTags: ['RoomPosting', 'Bookings', 'Specific Booking'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
    }),

    postFolioZeroRated: builder.mutation({
      query: (payload) => ({
        url: 'roomposting/zero-rated',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      invalidatesTags: ['RoomPosting', 'Bookings', 'Specific Booking'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
    }),

    postCityLedgerApproval: builder.mutation({
      query: (payload) => {
        const { invalidateTags, ...body } = payload;
        return {
          url: 'roomposting/clapproval',
          method: 'POST',
          body: encryptPayload(body),
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (!arg?.invalidateTags) {
            dispatch(
              api.util.invalidateTags([
                'RoomPosting',
                'Bookings',
                'Specific Booking',
              ]),
            );
            return;
          }
          dispatch(api.util.invalidateTags(['RoomPosting']));
        } catch (err) {
          // optional error handling
        }
      },
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
    }),

    postCreditMemo: builder.mutation({
      query: (payload) => ({
        url: 'roomposting/credit-memo',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      invalidatesTags: ['RoomPosting', 'Bookings', 'Specific Booking'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
    }),

    getCreditMemo: builder.query({
      query: (params) => {
        return `roomposting/credit-memo/${params?.cmID || '0'}/${
          params?.voided || 'ALL'
        }`;
      },
      providesTags: ['RoomPosting'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),

    postCityLedgerPaymentBreakdown: builder.mutation({
      query: (payload) => ({
        url: 'roomposting/cl-payment/',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      invalidatesTags: ['RoomPosting', 'Bookings', 'Specific Booking'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
    }),
  }),
});

export const {
  useGetEntriesQuery,
  useGetLedgerListQuery,
  usePostTransactionEntriesMutation,
  useLazyGetFolioUuidByRoomNoQuery,
  useGetSubGroupListQuery,
  usePostTransferSubGroupMutation,
  usePostModifyServiceChargeMutation,
  usePostModifyItemRemarksMutation,
  usePostRegularDiscountMutation,
  usePostSpecialDiscountMutation,
  useGetInHouseGuestQuery,
  usePostLineTransferMutation,
  usePostFolioItemVoidMutation,
  usePostFolioRemoveDiscountMutation,
  usePostFolioZeroRatedMutation,
  useLazyGetGuestLedgerQuery,
  usePostCityLedgerApprovalMutation,
  usePostCreditMemoMutation,
  useLazyGetCreditMemoQuery,
  usePostCityLedgerPaymentBreakdownMutation,
} = roomPostingApi;
